<script setup>
import { ref, computed, watch, reactive, onMounted, onUnmounted } from "vue";
import DisplayConditions from "./DisplayConditions.vue";
import ButtonComponent from "./ButtonComponent.vue";
import { uuidv4 } from "../utils/shared.js";
import AppIcon from "./AppIcon.vue";
import { config } from "../utils/consts.js";
import { vAutogrow } from "../utils/ui.js";

const UI_DETAILS = {
  card: {
    windowTitle: "Edit Card",
    linkTitle: "Links",
    showLinks: true,
    showMeta: true,
    showLinkDelete: true,
    isNotOps: true,
    onlyLinks: false,
    logicLabel: "Display logic",
  },
  logic: {
    windowTitle: "Edit Logic Block",
    linkTitle: "Links",
    showLinks: true,
    showMeta: false,
    showLinkDelete: true,
    isNotOps: true,
    onlyLinks: true,
    logicLabel: "Link conditions",
  },
  operator: {
    windowTitle: "Edit Operations",
    linkTitle: "Operations",
    showLinks: false,
    showMeta: false,
    showLinkDelete: false,
    isNotOps: false,
    onlyLinks: true,
    logicLabel: "Operations",
  },
  meta: {
    windowTitle: "Edit Meta",
    linkTitle: "Meta",
    showLinks: false,
    showMeta: true,
    showLinkDelete: false,
    isNotOps: false,
    onlyLinks: false,
    logicLabel: "Meta",
  },
  detour: {
    windowTitle: "Edit Detour",
    linkTitle: "Links",
    showLinks: false,
    showMeta: false,
    showLinkDelete: false,
    isNotOps: false,
    onlyLinks: false,
    logicLabel: "",
  },
  note: {
    windowTitle: "Edit Note",
    showLinks: false,
    showMeta: false,
    showLinkDelete: false,
    isNotOps: false,
    onlyLinks: false,
    logicLabel: "",
  },
};

const OPERATOR_OPERATORS = {
  eq: "=",
  inc: "+",
  dec: "-",
  rnd: "rnd",
  eval: "=eval()",
};

const CONDITION_OPTIONS = ["and", "or"];

const props = defineProps({
  editingCard: {
    type: String,
    required: true,
    validator: (value) => value === "none" || typeof value === "string",
  },
  storyData: {
    type: Object,
    required: true,
    validator: (value) => value.stories && value.variableDefs && value.loc,
  },
  edit: {
    type: Object,
    required: true,
    validator: (value) => value.currentStorylet && value.editingCard,
  },
});

const emit = defineEmits([
  "text-key",
  "lines-refresh",
  "edit-card",
  "follow-link",
  "edit-previous-card",
  "focus-paragraph",
  "focus-link",
  "link-add",
  "preview-card",
]);

const VIEW_MODE_KEY = 'cardEditViewMode';
function initViewMode() {
  const saved = localStorage.getItem(VIEW_MODE_KEY);
  if (saved === 'tab' || saved === 'scroll' || saved === 'wide') return saved;
  if (localStorage.getItem('cardEditScrollMode') === 'true') return 'scroll';
  return 'tab';
}
const viewMode = ref(initViewMode());
const effectiveViewMode = computed(() =>
  currentCard.value?.nodeType === 'card' ? viewMode.value : 'scroll'
);

function setViewMode(mode) {
  viewMode.value = mode;
  localStorage.setItem(VIEW_MODE_KEY, mode);
}

// Computed properties
const currentCard = computed(() => {
  return props.storyData?.stories[props.edit.currentStorylet]?.cards[
    props.editingCard
  ];
});

const nodeTypeDetails = computed(() => {
  const nodeType = currentCard.value?.nodeType;
  return nodeType ? UI_DETAILS[nodeType] : {};
});

const windowHeading = computed(
  () => nodeTypeDetails.value.windowTitle || "..."
);

// Vue-driven tab state — only cycles through tabs that exist for the current node type
const availableTabs = computed(() => {
  const type = currentCard.value?.nodeType;
  if (!type || type === "note") return [];
  const tabs = [];
  if (type === "card") tabs.push("paragraphs");
  if (type === "card" || type === "logic" || type === "operator") tabs.push("links");
  if (UI_DETAILS[type]?.showMeta) tabs.push("meta");
  return tabs;
});

const currentTab = ref("paragraphs");

watch(
  () => props.editingCard,
  () => {
    const tabs = availableTabs.value;
    if (tabs.length && !tabs.includes(currentTab.value)) {
      currentTab.value = tabs[0];
    }
  }
);

function cycleTab(forward = true) {
  const tabs = availableTabs.value;
  if (tabs.length <= 1) return;
  const idx = tabs.indexOf(currentTab.value);
  currentTab.value = tabs[forward
    ? (idx + 1) % tabs.length
    : (idx - 1 + tabs.length) % tabs.length];
}

function onTabKeydown(e) {
  if (props.editingCard === "none") return;
  if (e.key !== "Tab") return;
  const active = document.activeElement;
  if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT")) return;
  e.preventDefault();
  cycleTab(!e.shiftKey);
}

onMounted(() => document.addEventListener("keydown", onTabKeydown));
onUnmounted(() => document.removeEventListener("keydown", onTabKeydown));

const paragraphCount = computed(
  () => currentCard.value?.paragraphs?.length || 0
);
const linkCount = computed(() => currentCard.value?.links?.length || 0);

// Methods
function addTextKey() {
  const id = uuidv4();
  props.storyData.loc[id] = {};
  const keys = Object.keys(props.storyData.loc["na"]);
  keys.forEach((key) => {
    props.storyData.loc[id][key] = "...";
  });
  return id;
}

function addParagraph(card) {
  const textKey = addTextKey();
  props.storyData.stories[props.edit.currentStorylet].cards[
    card
  ].paragraphs.push({
    text: textKey,
    conditions: [],
  });
  emit(
    "focus-paragraph",
    props.storyData.stories[props.edit.currentStorylet].cards[card].paragraphs
      .length - 1
  );
}

function addCustom(card) {
  props.storyData.stories[props.edit.currentStorylet].cards[card].custom.push({
    customKey: "",
    customValue: "",
  });
}

function deleteCustom(card, index) {
  props.storyData.stories[props.edit.currentStorylet].cards[card].custom.splice(
    index,
    1
  );
}

function deleteParagraph(card, index) {
  const textKey =
    props.storyData.stories[props.edit.currentStorylet].cards[card].paragraphs[
      index
    ].text;
  props.storyData.stories[props.edit.currentStorylet].cards[
    card
  ].paragraphs.splice(index, 1);
  delete props.storyData.loc[textKey];
}

function deleteLink(card, index) {
  props.storyData.stories[props.edit.currentStorylet].cards[card].links.splice(
    index,
    1
  );
}

function followLink(card, index) {
  const target =
    props.storyData.stories[props.edit.currentStorylet].cards[card].links[index]
      .target;
  emit("follow-link", target);
}

function editPreviousCard() {
  emit("edit-previous-card");
}

function addLink(card) {
  emit("link-add", card);
}

function deleteCondition([index, paragraphIndex, context]) {
  const card =
    props.storyData.stories[props.edit.currentStorylet].cards[
    props.editingCard
    ];

  if (context === "paragraph" && card.paragraphs[paragraphIndex]?.conditions) {
    card.paragraphs[paragraphIndex].conditions.splice(index, 1);
  } else if (context === "link" && card.links[paragraphIndex]?.conditions) {
    card.links[paragraphIndex].conditions.splice(index, 1);
  } else if (
    context === "operations" &&
    card.links[paragraphIndex]?.operations
  ) {
    card.links[paragraphIndex].operations.splice(index, 1);
  }
}

function addCondition(card, index) {
  const item =
    props.storyData.stories[props.edit.currentStorylet].cards[card].paragraphs[
    index
    ];
  if (item.conditions) {
    item.conditions.push({ variable: "", operator: "", value: "" });
  }
  if (item.operators) {
    item.operators.push({ variable: "", operator: "", value: "" });
  }
}

function addLinkCondition(card, index) {
  const item =
    props.storyData.stories[props.edit.currentStorylet].cards[card].links[
    index
    ];
  if (item.conditions) {
    item.conditions.push({ variable: "", operator: "", value: "" });
  }
  if (item.operations) {
    item.operations.push({ variable: "", operator: "", value: "" });
  }
}

function focusParagraph(index) {
  emit("focus-paragraph", index);
}

function isCard() {
  const card =
    props.storyData.stories[props.edit.currentStorylet].cards[
    props.edit.editingCard
    ];
  if (card.nodeType == "card") {
    return true;
  }

  return false;
}

function linkNavState(link) {
  if (!link.target) return 'unlinked';
  const cards = props.storyData.stories[props.edit.currentStorylet]?.cards;
  if (!cards) return 'unlinked';
  const targetCard = cards[link.target];
  if (!targetCard) return 'unlinked';
  if (targetCard.nodeType === 'end') return 'hidden';
  if (link.target === props.editingCard) return 'unlinked';
  return 'linked';
}

function addCardForLink(linkIndex) {
  const id = uuidv4();
  const src = currentCard.value;
  const newCard = JSON.parse(JSON.stringify(config.newCard));
  newCard.editor.left = (src.editor?.left ?? 400) + 118 * 2;
  newCard.editor.top = src.editor?.top ?? 200;
  props.storyData.stories[props.edit.currentStorylet].cards[id] = newCard;
  src.links[linkIndex].target = id;
  emit('edit-card', id);
  emit('lines-refresh');
}

// Drag-to-sort
const drag = reactive({ type: null, from: null, over: null });

function dragStart(type, index) {
  drag.type = type;
  drag.from = index;
  drag.over = index;
}

function dragOver(type, index) {
  if (drag.type !== type) return;
  drag.over = index;
}

function dragEnd(arr) {
  if (drag.from !== null && drag.over !== null && drag.from !== drag.over) {
    const [item] = arr.splice(drag.from, 1);
    const insertAt = drag.over > drag.from ? drag.over - 1 : drag.over;
    arr.splice(insertAt, 0, item);
  }
  drag.type = null;
  drag.from = null;
  drag.over = null;
}

function setActivePanel(paragraph, panel) {
  paragraph.editor.panel = panel;
}

function panelVisible(item, panel) {
  if (!item.editor) {
    item.editor = { panel: nodeTypeDetails.value.onlyLinks ? 2 : 1 };
  }

  if (nodeTypeDetails.value.onlyLinks) {
    return panel === 2;
  }

  return item.editor.panel === panel;
}
</script>

<template>
  <div class="win-shade" :class="{ 'wide-view': effectiveViewMode === 'wide' }" v-if="editingCard != 'none'">
    <div class="edit-card ui-container" :class="{ 'small-win': currentCard?.nodeType !== 'card' }">

      <!-- Note editing: simple textarea, no tabs -->
      <div v-if="currentCard?.nodeType === 'note'" class="note-edit-content">
        <div class="ui-sub-container">
          <div class="ui-form-textinput">
            <textarea class="textarea paragraphtext note-textarea" v-model="currentCard.text" v-autogrow
              placeholder="Write your note here..."></textarea>
          </div>
        </div>
      </div>

      <div v-else class="tabbed" :class="{ 'scroll-view': effectiveViewMode === 'scroll', 'wide-view': effectiveViewMode === 'wide' }">

        <!-- Tab bar (tab mode only) -->
        <ul class="tabs" v-if="effectiveViewMode === 'tab'">
          <li class="tab noselect" :class="{ 'tab-active': currentTab === 'paragraphs' }"
            v-if="currentCard?.nodeType === 'card'" @click="currentTab = 'paragraphs'">
            <label>Paragraphs ({{ paragraphCount }})
              <span class="tab-add" @click.stop="addParagraph(editingCard)" title="Add paragraph"><AppIcon name="plus" /></span>
            </label>
          </li>
          <li class="tab noselect" :class="{ 'tab-active': currentTab === 'links' }" v-if="nodeTypeDetails.showLinks"
            @click="currentTab = 'links'">
            <label>{{ nodeTypeDetails.linkTitle }} ({{ linkCount }})
              <span v-if="nodeTypeDetails.isNotOps" class="tab-add" @click.stop="addLink(editingCard)"
                title="Add link"><AppIcon name="plus" /></span>
            </label>
          </li>
          <li class="tab noselect" :class="{ 'tab-active': currentTab === 'meta' }"
            v-if="nodeTypeDetails.showMeta && currentCard?.nodeType !== 'meta'" @click="currentTab = 'meta'">
            <label>Meta</label>
          </li>
        </ul>

        <!-- Content area -->
        <div :class="{ 'scroll-mode-body': effectiveViewMode === 'scroll', 'wide-mode-body': effectiveViewMode === 'wide' }">

          <!-- Left column: Paragraphs -->
          <div :class="effectiveViewMode === 'wide' ? 'wide-col' : null">

            <!-- Paragraphs section header (non-tab mode) -->
            <div v-if="effectiveViewMode !== 'tab' && currentCard?.nodeType === 'card'" class="scroll-section-head">
              <span>Paragraphs ({{ paragraphCount }})</span>
              <ButtonComponent label="" icon="plus" color="plain-selected" size="small" tooltip="Add paragraph"
                @click="addParagraph(editingCard)" />
            </div>

            <!-- Paragraphs -->
            <div id="paragraph-scroll" class="tab-content"
              v-show="currentCard?.nodeType === 'card' && (effectiveViewMode !== 'tab' || currentTab === 'paragraphs')">
              <div class="ui-sub-container">
                <template v-for="(paragraph, paragraphIndex) in currentCard.paragraphs" :key="paragraphIndex">
                  <div v-if="drag.type === 'paragraph' && drag.over === paragraphIndex && drag.from !== paragraphIndex"
                    class="drag-placeholder" />
                  <div class="ui-listshelf-container"
                    :class="{ 'drag-source': drag.type === 'paragraph' && drag.from === paragraphIndex }"
                    @dragover.prevent="dragOver('paragraph', paragraphIndex)"
                    @drop.prevent>
                  <div class="row">
                    <div class="drag-handle" draggable="true"
                      @dragstart="dragStart('paragraph', paragraphIndex)"
                      @dragend="dragEnd(currentCard.paragraphs)">
                      <AppIcon name="grip-vertical" />
                    </div>
                    <div class="column" v-if="panelVisible(paragraph, 1)">
                      <div class="ui-form-textinput">
                        <textarea class="textarea paragraphtext" @click="focusParagraph(paragraphIndex)" v-autogrow
                          v-model="storyData.loc[paragraph.text][storyData.currentLanguage]
                          "></textarea>
                      </div>
                    </div>
                    <div class="column" v-if="panelVisible(paragraph, 2)">
                      <div class="row">
                        <div class="ui-form-textinput column grow" v-if="paragraph.conditions.length > 1">
                          <select v-model="paragraph.conditionAndOr" class="ui-select condition-col-1">
                            <option v-for="option in CONDITION_OPTIONS" :key="option" :value="option">
                              {{ option }}
                            </option>
                          </select>
                        </div>
                        <div class="column right pad-button">
                          <ButtonComponent label="" icon="plus" color="plain-selected" size="small"
                            tooltip="Add condition" @click="addCondition(editingCard, paragraphIndex)" />
                        </div>
                      </div>
                      <div class="ui-form-textinput">
                        <DisplayConditions v-for="(condition, index) in paragraph.conditions" :key="index"
                          :variables="storyData.variableDefs" :condition="condition" :context="'paragraph'"
                          :conditionIndex="index" :paragraphIndex="paragraphIndex" @delete-condition="deleteCondition" />
                      </div>
                    </div>
                    <div class="column-s">
                      <div class="grow">
                        <div :class="panelVisible(paragraph, 1) ? 'minitab-selected' : 'minitab'">
                          <ButtonComponent label="" icon="comment" :color="panelVisible(paragraph, 1) ? 'plain-selected' : 'plain'"
                            size="small" tooltip="Text" @click="setActivePanel(paragraph, 1)" />
                        </div>
                        <div :class="panelVisible(paragraph, 2) ? 'minitab-selected' : 'minitab'">
                          <ButtonComponent label="" icon="wand-magic-sparkles" :color="panelVisible(paragraph, 2) ? 'plain-selected' : 'plain'"
                            size="small" tooltip="Conditions" @click="setActivePanel(paragraph, 2)" />
                          <div class="minitab-count">
                            {{ paragraph.conditions.length }}
                          </div>
                        </div>
                      </div>
                      <div class="minitab">
                        <ButtonComponent label="" icon="trash" color="plain-selected" size="small"
                          tooltip="Delete paragraph" @click="deleteParagraph(editingCard, paragraphIndex)" />
                      </div>
                    </div>
                  </div>
                  </div>
                </template>
                <div v-if="drag.type === 'paragraph' && drag.over === currentCard.paragraphs.length"
                  class="drag-placeholder" />
                <div v-if="drag.type === 'paragraph'"
                  class="drag-end-zone"
                  @dragover.prevent="dragOver('paragraph', currentCard.paragraphs.length)"
                  @drop.prevent />
              </div>
            </div>

          </div><!-- end left col -->

          <!-- Right column: Links + Meta -->
          <div :class="effectiveViewMode === 'wide' ? 'wide-col' : null">

            <!-- Links section header (non-tab mode) -->
            <div v-if="effectiveViewMode !== 'tab' && nodeTypeDetails.showLinks" class="scroll-section-head">
              <span>{{ nodeTypeDetails.linkTitle }} ({{ linkCount }})</span>
              <ButtonComponent v-if="nodeTypeDetails.isNotOps" label="" icon="plus" color="plain-selected" size="small"
                tooltip="Add link" @click="addLink(editingCard)" />
            </div>

            <!-- Links -->
            <div class="tab-content" v-show="(currentCard?.nodeType == 'card' || currentCard?.nodeType == 'logic' || currentCard?.nodeType == 'operator') &&
              (effectiveViewMode !== 'tab' || currentTab === 'links')">
              <div class="ui-sub-container">
                <template v-for="(link, linkIndex) in currentCard?.links" :key="linkIndex">
                  <div v-if="drag.type === 'link' && drag.over === linkIndex && drag.from !== linkIndex"
                    class="drag-placeholder" />
                  <div class="ui-listshelf-container"
                    :class="{ 'drag-source': drag.type === 'link' && drag.from === linkIndex }"
                    @dragover.prevent="dragOver('link', linkIndex)"
                    @drop.prevent>
                  <div class="row">
                    <div class="drag-handle" draggable="true"
                      @dragstart="dragStart('link', linkIndex)"
                      @dragend="dragEnd(currentCard.links)">
                      <AppIcon name="grip-vertical" />
                    </div>
                    <div class="column" v-if="nodeTypeDetails.isNotOps && panelVisible(link, 1)">
                      <div class="ui-form-textinput">
                        <input class="link-input" type="text"
                          v-model="storyData.loc[link.linkText][storyData.currentLanguage]" />
                        <ButtonComponent v-if="linkNavState(link) === 'linked'" label="" icon="arrow-right" color="blue"
                          size="small" tooltip="Edit linked card" @click="followLink(editingCard, linkIndex)" />
                        <ButtonComponent v-else-if="linkNavState(link) === 'unlinked'" label="" icon="plus" color="green"
                          size="small" tooltip="Create and link a card" @click="addCardForLink(linkIndex)" />
                      </div>
                    </div>
                    <div class="column" v-if="panelVisible(link, 2)">
                      <div class="row">
                        <div class="ui-form-textinput column grow" v-if="link.conditions?.length > 1">
                          <select v-model="link.conditionAndOr" class="ui-select condition-col-1">
                            <option v-for="option in CONDITION_OPTIONS" :key="option" :value="option">
                              {{ option }}
                            </option>
                          </select>
                        </div>
                        <div class="column right pad-button">
                          <ButtonComponent label="" icon="plus" color="plain-selected" size="small"
                            tooltip="Add condition" @click="addLinkCondition(editingCard, linkIndex)" />
                        </div>
                      </div>
                      <div class="ui-form-textinput" v-if="link.conditions">
                        <DisplayConditions v-for="(condition, index) in link.conditions" :key="index"
                          :variables="storyData.variableDefs" :condition="condition" :context="'link'"
                          :conditionIndex="index" :paragraphIndex="linkIndex" @delete-condition="deleteCondition" />
                      </div>
                      <div class="ui-form-textinput" v-if="link.operations">
                        <div v-for="(operator, index) in link.operations" :key="index" class="row">
                          <div class="pad-s">
                            <select v-model="operator.variable" class="ui-select condition-col-1">
                              <option v-for="(vari, varIndex) in storyData.variableDefs" :key="varIndex"
                                :value="varIndex">
                                {{ varIndex }}
                              </option>
                            </select>
                          </div>
                          <div class="pad-s">
                            <select v-model="operator.operator" class="ui-select condition-col-2">
                              <option v-for="(op, opIndex) in OPERATOR_OPERATORS" :key="opIndex" :value="opIndex">
                                {{ op }}
                              </option>
                            </select>
                          </div>
                          <div class="pad-s">
                            <select v-model="operator.value" class="ui-select condition-col-3" v-if="
                              storyData.variableDefs[operator.variable]?.type === 'enum'">
                              <option v-for="(en, enumIndex) in storyData.variableDefs[operator.variable]?.enumValues"
                                :key="enumIndex" :value="en">
                                {{ en }}
                              </option>
                            </select>
                            <input type="text" v-model="operator.value" v-else />
                          </div>
                          <div class="pad-s">
                            <ButtonComponent :label="null" icon="trash" color="red" size="large"
                              tooltip="Delete operation" @click="deleteCondition([index, linkIndex, 'operations'])" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="column-s">
                      <div class="grow">
                        <div :class="panelVisible(link, 1) ? 'minitab-selected' : 'minitab'" v-if="isCard()">
                          <ButtonComponent label="" icon="comment" :color="panelVisible(link, 1) ? 'plain-selected' : 'plain'"
                            size="small" tooltip="Text" @click="setActivePanel(link, 1)" />
                        </div>
                        <div :class="panelVisible(link, 2) ? 'minitab-selected' : 'minitab'">
                          <ButtonComponent label="" icon="wand-magic-sparkles" :color="panelVisible(link, 2) ? 'plain-selected' : 'plain'"
                            size="small" tooltip="Conditions" @click="setActivePanel(link, 2)" />
                          <div class="minitab-count" v-if="link.conditions">
                            {{ link.conditions.length }}
                          </div>
                        </div>
                      </div>
                      <div v-if="nodeTypeDetails.showLinkDelete" class="minitab">
                        <ButtonComponent label="" icon="trash" color="plain-selected" size="small" tooltip="Delete link"
                          @click="deleteLink(editingCard, linkIndex)" />
                      </div>
                    </div>
                  </div>
                  </div>
                </template>
                <div v-if="drag.type === 'link' && drag.over === currentCard.links.length"
                  class="drag-placeholder" />
                <div v-if="drag.type === 'link'"
                  class="drag-end-zone"
                  @dragover.prevent="dragOver('link', currentCard.links.length)"
                  @drop.prevent />
              </div>
            </div>

            <!-- Meta section header (non-tab mode) -->
            <div v-if="effectiveViewMode !== 'tab' && nodeTypeDetails.showMeta && currentCard?.nodeType !== 'meta'"
              class="scroll-section-head">
              <span>Meta</span>
            </div>

            <!-- Meta -->
            <div class="tab-content"
              v-show="nodeTypeDetails.showMeta && (currentCard?.nodeType === 'meta' || effectiveViewMode !== 'tab' || currentTab === 'meta')">
              <div class="ui-sub-container row">
                <div class="ui-controls column">
                  <div class="ui-form-textinput">
                    <div class="ui-label">Description</div>
                    <input type="text" v-model="currentCard.description" />
                  </div>
                </div>
                <div class="ui-controls column">
                  <div class="ui-form-textinput">
                    <div class="ui-label">Tags</div>
                    <input type="text" v-model="currentCard.tags" />
                  </div>
                </div>
              </div>
              <div class="ui-sub-container">
                <div v-for="(custom, index) in currentCard.custom" :key="index" class="ui-controls row">
                  <div>
                    <div class="ui-form-textinput">
                      <div class="ui-label" v-if="index == 0">Key</div>
                      <input type="text" v-model="custom.customKey" />
                    </div>
                  </div>
                  <div>
                    <div class="ui-form-textinput">
                      <div class="ui-label" v-if="index == 0">Value</div>
                      <input type="text" v-model="custom.customValue" />
                    </div>
                  </div>
                  <div class="pad-l end">
                    <ButtonComponent :label="null" icon="trash" color="red" size="large" tooltip="Delete"
                      @click="deleteCustom(editingCard, index)" />
                  </div>
                </div>
                <div class="ui-sub-container pad-l">
                  <ButtonComponent :label="'Add meta key/value'" icon="plus" color="green" size="medium"
                    @click="addCustom(editingCard)" />
                </div>
              </div>
            </div>

          </div><!-- end right col -->

        </div><!-- end content area -->
      </div>

      <div class="window-title">
        <ButtonComponent label="" icon="arrow-left" color="blue" size="small"
          tooltip="Go back to previous card" :disabled="!edit.canGoBack"
          @click="editPreviousCard()" />
        <h3>{{ windowHeading }}</h3>
        <div class="cornerbutton">
          <template v-if="currentCard?.nodeType === 'card'">
            <ButtonComponent label="" icon="eye" color="plain" size="small" tooltip="Preview this card"
              @click="emit('preview-card')" />
            <ButtonComponent label="" icon="table-columns" :color="effectiveViewMode === 'tab' ? 'plain-selected' : 'plain'"
              size="small" tooltip="Tab view" @click="setViewMode('tab')" />
            <ButtonComponent label="" icon="bars" :color="effectiveViewMode === 'scroll' ? 'plain-selected' : 'plain'"
              size="small" tooltip="Scroll view" @click="setViewMode('scroll')" />
            <ButtonComponent label="" icon="arrows-left-right" :color="effectiveViewMode === 'wide' ? 'plain-selected' : 'plain'"
              size="small" tooltip="Wide view" @click="setViewMode('wide')" />
          </template>
          <button @click="edit.editingCard = 'none'" title="Close (Esc)" class="ui-button ui-button-blue">
            <AppIcon name="xmark" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
